import React, { useState } from "react";
import { Button, useToast, Icon } from "@chakra-ui/react";
import { FaApple } from "react-icons/fa";
import axios from "axios";
import { apiUrl } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import { isAppleWalletCapable } from "../utils/platform";

const AddToAppleWalletButton = ({ campaignId, userId, size = "md" }) => {
  const toast = useToast();
  const { translate } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);

  const disabled = !campaignId || !userId;

  const handleClick = async () => {
    if (!isAppleWalletCapable()) {
      toast({
        title: translate("wallet.iosOnlyTitle"),
        description: translate("wallet.iosOnlyMessage"),
        status: "info",
        duration: 6000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    toast({
      title: translate("wallet.downloadingTitle"),
      description: translate("wallet.downloadingMessage"),
      status: "info",
      duration: 3000,
      isClosable: true,
    });

    try {
      const response = await axios.get(
        `${apiUrl}/wallet/apple/${campaignId}/${userId}`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.apple.pkpass",
      });
      const objectUrl = window.URL.createObjectURL(blob);
      // On iOS Safari, navigating to the blob triggers the Wallet "Add" sheet.
      // Using a synthetic anchor with download fallback handles in-app browsers.
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `coupal-${campaignId}.pkpass`;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Defer revocation so Safari has time to hand the blob to Wallet.
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 30000);
    } catch (error) {
      const status = error.response?.status;
      if (status === 503) {
        toast({
          title: translate("wallet.notConfiguredTitle"),
          description: translate("wallet.notConfiguredMessage"),
          status: "warning",
          duration: 6000,
          isClosable: true,
        });
      } else {
        toast({
          title: translate("wallet.errorTitle"),
          description: translate("wallet.errorMessage"),
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      onClick={handleClick}
      isLoading={isLoading}
      isDisabled={disabled}
      size={size}
      bg="black"
      color="white"
      _hover={{ bg: "gray.800" }}
      _active={{ bg: "gray.900" }}
      leftIcon={<Icon as={FaApple} boxSize={5} />}
      borderRadius="md"
      fontWeight="semibold"
    >
      {translate("wallet.addToAppleWallet")}
    </Button>
  );
};

export default AddToAppleWalletButton;
